import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { listQuizzes, getQuizResults, getAttemptResults, gradeQuizResponse } from '../services/quizService';
import { getCourses } from '../services/courseService';
import { Course } from '../types';

const QuizzesResultsReview: React.FC = () => {
  const { user } = useAuth();
  const [courses, setCourses] = useState<Course[]>([]);
  const [selectedCourseId, setSelectedCourseId] = useState<number | 'all'>('all');
  const [quizzes, setQuizzes] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedQuizId, setExpandedQuizId] = useState<number | null>(null);
  const [quizResults, setQuizResults] = useState<{ [quizId: number]: any[] }>({});
  const [selectedAttempt, setSelectedAttempt] = useState<any | null>(null);
  const [attemptDetails, setAttemptDetails] = useState<any | null>(null);
  const [grading, setGrading] = useState<{ [responseId: number]: boolean }>({});
  const [courseMap, setCourseMap] = useState<{ [id: number]: string }>({});
  // Filters
  const [statusFilter, setStatusFilter] = useState<'all' | 'submitted' | 'in_progress'>('all');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');

  useEffect(() => {
    async function fetchCourses() {
      if (!user?.school_id) return;
      try {
        const data = await getCourses(user.school_id);
        setCourses(data);
        const map: { [id: number]: string } = {};
        data.forEach((c) => { map[c.id] = c.title; });
        setCourseMap(map);
      } catch (e: any) {
        // ignore for now
      }
    }
    fetchCourses();
  }, [user?.school_id]);

  useEffect(() => {
    async function fetchQuizzes() {
      setLoading(true);
      setError(null);
      try {
        const all = await listQuizzes();
        // Only quizzes created by this user
        let mine = all.filter((q: any) => q.created_by === user?.id);
        if (selectedCourseId !== 'all') {
          mine = mine.filter((q: any) => q.course_id === selectedCourseId);
        }
        setQuizzes(mine);
      } catch (e: any) {
        setError(e?.response?.data?.detail || 'Failed to load quizzes');
      } finally {
        setLoading(false);
      }
    }
    fetchQuizzes();
  }, [user?.id, selectedCourseId]);

  const handleExpandQuiz = async (quizId: number) => {
    setExpandedQuizId(expandedQuizId === quizId ? null : quizId);
    if (!quizResults[quizId]) {
      try {
        const results = await getQuizResults(quizId);
        setQuizResults((prev) => ({ ...prev, [quizId]: results as any[] }));
      } catch (e: any) {
        setError(e?.response?.data?.detail || 'Failed to load quiz results');
      }
    }
  };

  const handleViewAttempt = async (attempt: any) => {
    setSelectedAttempt(attempt);
    setAttemptDetails(null);
    try {
      const details = await getAttemptResults(attempt.attempt_id || attempt.id);
      setAttemptDetails(details);
    } catch (e: any) {
      setAttemptDetails({ error: e?.response?.data?.detail || 'Failed to load details' });
    }
  };

  const handleGrade = async (responseId: number, isCorrect: boolean) => {
    setGrading((prev) => ({ ...prev, [responseId]: true }));
    try {
      await gradeQuizResponse(responseId, isCorrect);
      // Refresh attempt details
      if (selectedAttempt) {
        const details = await getAttemptResults(selectedAttempt.attempt_id || selectedAttempt.id);
        setAttemptDetails(details);
      }
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Failed to grade response');
    } finally {
      setGrading((prev) => ({ ...prev, [responseId]: false }));
    }
  };

  // Filter attempts by status and date
  function filterAttempts(attempts: any[]) {
    return attempts.filter(a => {
      if (statusFilter === 'submitted' && !a.completed_at) return false;
      if (statusFilter === 'in_progress' && a.completed_at) return false;
      if (dateFrom && new Date(a.started_at) < new Date(dateFrom)) return false;
      if (dateTo && new Date(a.started_at) > new Date(dateTo)) return false;
      return true;
    });
  }

  return (
    <div>
      <h1>Quiz Results Review</h1>
      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginBottom: 16 }}>
        <div>
          <label htmlFor="course-filter"><b>Course:</b> </label>
          <select
            id="course-filter"
            value={selectedCourseId}
            onChange={e => setSelectedCourseId(e.target.value === 'all' ? 'all' : Number(e.target.value))}
            style={{ marginLeft: 8 }}
          >
            <option value="all">All Courses</option>
            {courses.map(c => (
              <option key={c.id} value={c.id}>{c.title}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="status-filter"><b>Status:</b> </label>
          <select
            id="status-filter"
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value as any)}
            style={{ marginLeft: 8 }}
          >
            <option value="all">All</option>
            <option value="submitted">Submitted</option>
            <option value="in_progress">In Progress</option>
          </select>
        </div>
        <div>
          <label><b>Date From:</b> </label>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={{ marginLeft: 8 }} />
        </div>
        <div>
          <label><b>Date To:</b> </label>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={{ marginLeft: 8 }} />
        </div>
      </div>
      {loading ? (
        <p>Loading...</p>
      ) : error ? (
        <p style={{ color: 'red' }}>{error}</p>
      ) : quizzes.length === 0 ? (
        <p>No quizzes found for review.</p>
      ) : (
        <div>
          {quizzes.map((quiz) => (
            <div key={quiz.id} style={{ border: '1px solid #ccc', borderRadius: 8, margin: '16px 0', padding: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <b>{quiz.title}</b> <span style={{ color: '#888' }}>({quiz.mode})</span>
                  <span style={{ marginLeft: 16, color: '#555' }}>Course: {courseMap[quiz.course_id] || quiz.course_id}</span>
                </div>
                <button onClick={() => handleExpandQuiz(quiz.id)}>
                  {expandedQuizId === quiz.id ? 'Hide Attempts' : 'Show Attempts'}
                </button>
              </div>
              {expandedQuizId === quiz.id && (
                <div style={{ marginTop: 12 }}>
                  {!quizResults[quiz.id] ? (
                    <p>Loading attempts...</p>
                  ) : filterAttempts(quizResults[quiz.id]).length === 0 ? (
                    <p>No attempts match the selected filters.</p>
                  ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr>
                          <th>Student</th>
                          <th>Score</th>
                          <th>Status</th>
                          <th>Date</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filterAttempts(quizResults[quiz.id]).map((a: any) => (
                          <tr key={a.attempt_id || a.id}>
                            <td>{a.student_id}</td>
                            <td>{a.score !== null && a.score !== undefined ? a.score : '-'}</td>
                            <td>{a.completed_at ? 'Submitted' : 'In Progress'}</td>
                            <td>{a.completed_at ? new Date(a.completed_at).toLocaleString() : '-'}</td>
                            <td>
                              <button onClick={() => handleViewAttempt(a)}>View / Grade</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Attempt Details Dialog */}
      {selectedAttempt && (
        <div style={{ position: 'fixed', top: 40, left: 0, right: 0, margin: 'auto', maxWidth: 700, background: '#fff', border: '1px solid #ccc', borderRadius: 8, padding: 24, zIndex: 1000 }}>
          <h2>Attempt Details</h2>
          <button style={{ float: 'right' }} onClick={() => { setSelectedAttempt(null); setAttemptDetails(null); }}>Close</button>
          {!attemptDetails ? (
            <p>Loading...</p>
          ) : attemptDetails.error ? (
            <p style={{ color: 'red' }}>{attemptDetails.error}</p>
          ) : (
            <div>
              <p><b>Student:</b> {attemptDetails.student_id}</p>
              <p><b>Score:</b> {attemptDetails.score}</p>
              <p><b>Status:</b> {selectedAttempt.completed_at ? 'Submitted' : 'In Progress'}</p>
              <p><b>Completed At:</b> {selectedAttempt.completed_at ? new Date(selectedAttempt.completed_at).toLocaleString() : '-'}</p>
              <h3>Responses</h3>
              <ul>
                {attemptDetails.responses && attemptDetails.responses.length > 0 ? attemptDetails.responses.map((resp: any, idx: number) => (
                  <li key={resp.id} style={{ color: resp.is_correct === true ? 'green' : resp.is_correct === false ? 'red' : '#888', marginBottom: 8 }}>
                    Q{idx + 1}: {resp.question_id} — {resp.is_correct === true ? 'Correct' : resp.is_correct === false ? 'Incorrect' : 'Pending Review'}
                    {resp.answer_text && <span> (Answer: {resp.answer_text})</span>}
                    {resp.selected_option_id && <span> (Option: {resp.selected_option_id})</span>}
                    {resp.is_correct === null && (
                      <span style={{ marginLeft: 16 }}>
                        <button disabled={grading[resp.id]} onClick={() => handleGrade(resp.id, true)}>Mark Correct</button>
                        <button disabled={grading[resp.id]} style={{ marginLeft: 8 }} onClick={() => handleGrade(resp.id, false)}>Mark Incorrect</button>
                      </span>
                    )}
                  </li>
                )) : <li>No responses found.</li>}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default QuizzesResultsReview; 